import { ConfidentialTransferMint } from "@/api/token2022/confidential_transfer";
import { Metadata } from "@metaplex-foundation/mpl-token-metadata";
import { web3 } from "@project-serum/anchor";
import { TransferFeeConfig, TransferHook } from "@solana/spl-token";
import { TokenMetadata } from "@solana/spl-token-metadata";

export type ERiskLevel = "normal" | "warn" | "danger";

export interface IRisk {
	level: ERiskLevel;
	name: string;
	description: string;
}

export interface IRisks extends Array<IRisk> {}

export interface IKnownToken {
	name: string;
	type: string;
}

export interface IToken {
	address: web3.PublicKey;
	amount: number;
	decimals: number;
	mint: string;
	name: string;
	programID: web3.PublicKey;
	supply: bigint;
	symbol: string;
	extensions: number[];
	complete?: boolean;
	image?: string;
	owner?: web3.PublicKey;
	mintAuthority?: web3.PublicKey;
	freezeAuthority?: web3.PublicKey;
	verified?: boolean;
	isInitialized?: boolean;
	tlvData?: Buffer;
	transferFee?: TransferFeeConfig;
	confidentialTransfer?: ConfidentialTransferMint;
	transferHook?: TransferHook;
	tokenMetadata?: TokenMetadata;
	metadata?: Metadata;

	createdAt?: number;
	createdBy?: web3.PublicKey;
}

export interface ITokenAccount {
	primitive?: {
		account: web3.AccountInfo<web3.ParsedAccountData>;
		pubkey: web3.PublicKey;
	};
	mint: string;
	address: web3.PublicKey;
	amount: number;
	decimals: number;
}

export interface ITokenBasic {
	address: web3.PublicKey;
	mint: string;
	decimals: number;
	name: string;
	symbol: string;
	amount: number;
	image?: string;
}

export interface ITokenDistribution {
	distribution: {
		fly: { count: number; value: number };
		worm: { count: number; value: number };
		shrimp: { count: number; value: number };
		crab: { count: number; value: number };
		shark: { count: number; value: number };
		dolphin: { count: number; value: number };
		whale: { count: number; value: number };
		unicorn: { count: number; value: number };
		dragon: { count: number; value: number };
	};
	total: { count: number; value: number };
}

export type ISettingsPriorityLevel = "market" | "high" | "turbo";

export interface ISettings {
	explorerOpens: "sidebar" | "tab";
	explorer: string;
	customRpcUrl: string;
	slippage: number;
	validatorTip: number | null;
	priorityFeeMax: number | null;
	priorityLevel: ISettingsPriorityLevel;
	validatorLevel: ISettingsPriorityLevel;
	swapValues: { sell: number[]; buy: number[] };
	instantBuy: boolean;
}

export interface ISession {
	lastToken: string;
}

export interface ITrade {
	amount: number;
	// date: string;
	// from: string;
	inMint: string;
	outMint: string;
	platform: string;
	price: number;
	program?: string;
	sell: boolean;
	// signature: string;
	signer: string;
	time: number;
	// to: string;
	value: number;
}

export interface ITrades extends Array<ITrade> {}

export interface IBar {
	time: number;
	open: number;
	close: number;
	high: number;
	low: number;
	volume: number;
	isBarClosed: boolean;
	isLastBar: boolean;
	count: number;
}

export interface IBars extends Array<IBar> {}

export enum EAccountType {
	Uninitialized,
	Mint,
	Account,
}

export interface IPoolDetail {
	tokenAccountA: any;
	tokenAccountB: any;
	tokenPool: any;
	userLP: any;
	mintA: any;
	mintB: any;
	mintProgramA: any;
	mintProgramB: any;
	mintLp: any;
	poolAddress: web3.PublicKey;
}

export interface IRoute {
	pool: any;
	detail?: IPoolDetail;
}

export interface IHolder {
	mint: string;
	currentBalance: number;
	buyAmount: number;
	buyValue: number;
	realisedValue: number;
	sellAmount: number;
	sellValue: number;
	trades: number;
	wallet: string;
	timestamp?: number;
}

export interface IHolders extends Array<IHolder> {}

export interface IPaginateEventParams {
	page: number;
	perPage: number;
	startIndex: number;
	endIndex: number;
}

export interface ITokenPriceUSD {
	mint: string;
	priceUSD: number;
	updateAt?: Date;
}

export interface IPnL {
	trades: number;
	buyAmount: number;
	sellAmount: number;
	currentBalance: number;
	decimals: number;
	buyValue: number;
	sellValue: number;
	realisedPnl: number;
	unrealisedPnl: number;
	pnl: number;
}

export interface ITradeOrder {
	id: string;
	method: ETradeOrderMethod;
	amount: number;
	amountPct: boolean;
	value: number;
	triggerPrice: number;
	delta: number;
	createdAt: Date;
	updatedAt: Date;
}

export type ITradeOrderType =
	| "LIMIT"
	| "TAKE_PROFIT"
	| "STOP_LOSS"
	| "STOP_LOSS_TRAILING";

export enum ETradeOrderMethod {
	LIMIT,
	STOP_LOSS,
	TAKE_PROFIT,
	STOP_LOSS_TRAILING,
}

export type ITradeOrderPlatform = "JUP" | "FLUXB";

export interface ITradeOrders extends Array<ITradeOrder> {}

export interface ITraderOrder {
	id: string;
	source: string;
	mint: string;
	amount: number;
	amountPct: boolean;
	value: number;
	method: ETradeOrderMethod;
	status: number;
}

export interface ITraderOrders extends Array<ITraderOrder> {}

export enum ECopyTradeMode {
	OFF,
	FIXED,
	PERCENT,
}

export enum ETokenSniperMode {
	SNIPER,
	SHOTGUN,
	GATLING,
}

export enum ETokenSniperPlatform {
	FLUXBEAM = 1 << 0,
	RAYDIUM = 1 << 1,
	METEORA = 1 << 2,
	METEORADLMM = 1 << 3,
	PUMPFUN = 1 << 4,
	ORCA = 1 << 5,
	DEGENFUND = 1 << 6,
	WHALESMEME = 1 << 7,
}

export interface INewToken {
	mint: string;
	address: web3.PublicKey;
	supply: number;
	decimals: number;
	program: web3.PublicKey;
	image: string;
}

export interface INewTokens extends Array<INewToken> {}

export interface ITab {
	name: string;
	reload?: boolean;
	redirect?: string;
}

export enum ESignalCategories {
	TOKEN = "tokens",
	// TRADER = "traders",
	// LENDING = "lending",
	INDICATOR = "technical_indicators",
	// SOCIAL = "social",
	CUSTOM = "custom",
}

export enum ESignalToken {
	TRADE = "token_trade",
	BUY = "token_buy",
	SELL = "token_sell",
	PRICE = "token_price",
	PRICE_CHANGE = "token_price_change",
	VOLUME_CHANGE = "token_volume_change",
	MINT = "token_mint",
	BURN = "token_burn",
	TRANSFER = "token_transfer",
}

export enum ESignalTrader {
	TRADE = "trader_trade",
	BUY = "trader_buy",
	SELL = "trader_sell",
	TRANSFER = "trader_transfer",
}

export enum ESignalLending {
	WITHDRAW = "mfi_withdraw",
	DEPOSIT = "mfi_deposit",
	BORROW = "mfi_borrow",
	LIQUIDATE = "mfi_liquidate",
	REPAY = "mfi_repay",
}

export enum ESignalIndicator {
	SMA = "simple_moving_average",
	EMA = "exponential_moving_average",
	OBV = "trading_indicator_obv",
	RSI = "relative_strength_index",
	MACD = "MACD",
	ADI = "ADI",
	SO = "sto_osc",
	AO = "aroon_osc",
	BO = "bollinger_bands",
}

export enum ESignalCustom {
	WEBHOOK = "custom_webhook",
}

export enum ESignalSocial {
	TWEET = "social_tweet",
	RETWEET = "social_retweet",
	QUOTE = "social_quote",
	NEWS = "social_news",
}

export type ESignals =
	| ESignalToken
	| ESignalTrader
	| ESignalLending
	| ESignalIndicator
	| ESignalCustom
	| ESignalCustom
	| ESignalSocial;

export enum EFilterOperators {
	EQUAL,
	NOT_EQUAL,
	LESS,
	LESS_EQUAL,
	GREATER,
	GREATER_EQUAL,
}

export const EFilterOperatorsAll = [
	EFilterOperators.EQUAL,
	EFilterOperators.NOT_EQUAL,
	EFilterOperators.LESS,
	EFilterOperators.LESS_EQUAL,
	EFilterOperators.GREATER,
	EFilterOperators.GREATER_EQUAL,
];
export const EFilterOperatorsParity = [
	EFilterOperators.EQUAL,
	EFilterOperators.NOT_EQUAL,
];
export const EFilterOperatorsChange = [
	EFilterOperators.LESS,
	EFilterOperators.GREATER,
];

export class FilterOperatorsBitmap {
	private bitmap: number = 0;

	constructor(enabledOperators: EFilterOperators[] = EFilterOperatorsAll) {
		enabledOperators.forEach((operator) => this.enable(operator));
	}

	enable(operator: EFilterOperators): void {
		this.bitmap |= 1 << operator;
	}

	disable(operator: EFilterOperators): void {
		this.bitmap &= ~(1 << operator);
	}

	isEnabled(operator: EFilterOperators): boolean {
		return (this.bitmap & (1 << operator)) !== 0;
	}

	getBitmap(): number {
		return this.bitmap;
	}

	setBitmap(bitmap: number): void {
		this.bitmap = bitmap;
	}
}

export enum EActionCategories {
	TRADE = "trade",
	LIMIT_ORDER = "limit_order",
}

export enum EActionTrade {
	SWAP = "swap",
	BUY = "buy",
	SELL = "sell",
}

export enum EActionLimitOrder {
	LIMIT_ORDER = "lo_create",
	TAKE_PROFIT = "take_profit",
	STOP_LOSS = "stop_loss",
	TRAILING_STOP_LOSS = "trailing_stop_loss",
}

export type EActions = EActionTrade | EActionLimitOrder;

export interface ISignal {
	id?: number;
	category: ESignalCategories;
	type: ESignals;
	event: ESignals;
	filters: ISignalFilters;
	data: any;
}

export interface ISignals extends Array<ISignal> {}

export interface ISignalFilter {
	name: string;
	type: string;
	key: string;
	operator: EFilterOperators;
	value: any;
}

export interface ISignalFilters extends Array<ISignalFilter> {}

export interface IAction {
	id?: number;
	category: EActionCategories;
	type: EActions;
	data: any;
}

export interface IActions extends Array<IAction> {}

export interface IStrategy {
	name: string;
	signals: ISignal[];
	actions: IAction[];
}

export interface IStrategies extends Array<IStrategy> {}

export interface IAutomation {
	id: string;
	status: string;
	name: string;
	wallet: string;
	strategy: IStrategy;
}

export interface IAutomations extends Array<IAutomation> {}

export interface IPost {
	id: string;
}
export interface IPostTrade extends IPost {
	user: {
		address: string;
		name: string;
		image: string;
	};
	token: {
		name: string;
		mint: string;
	};
	comments: IPostComments
	marketCap: number;
	description: string;
	bought: number;
	sold: number;
	holding: number;
	pnl: number;
	pnlPercent: number;
	createdAt: number;
}

export interface IPosts extends Array<IPost> {}

export interface IPostComment {
	comment: string;
	createdAt: number;
	user: {
		name: string;
		address: string;
		image: string;
	};
}

export interface IPostComments extends Array<IPostComment> {}
